import * as React from "react";
import { useState } from "react";
import { useQuery } from "@apollo/client";
import { SaveMeetingInput } from "@/api";
import Page from "@/components/main-frame/Page";
import { removeTypename } from "@/types";
import { copy } from "@/tools";
import { Link } from "@/components/tools";
import { UpdateMeetingQuery, UpdateMeetingQueryVariables, UpdateMeetingDocument } from "./queries.generated";
import SaveMeetingForm from "./SaveMeetingForm";
import { defineMessage } from "react-intl";

type InitialMeetingFormData = {
    formData: SaveMeetingInput;
    autoGeneratedInvitationIds: number[];
};

const useInitialMeetingFormData = (meetingId: number) => {
    const [result, setResult] = useState<InitialMeetingFormData>();

    useQuery<UpdateMeetingQuery, UpdateMeetingQueryVariables>(UpdateMeetingDocument, {
        variables: { meetingId },
        fetchPolicy: "cache-and-network",
        onCompleted: (origData) => {
            const data = copy(origData);
            removeTypename(data.meetings.items[0]);

            const autoGeneratedInvitationIds = [];
            for (const inv of data.invitations.items) {
                removeTypename(inv);
                if (inv.isAutoGenerated) autoGeneratedInvitationIds.push(inv.id);
                delete inv.isAutoGenerated;
            }

            const formData: SaveMeetingInput = {
                ...data.meetings.items[0],
                length: data.meetings.items[0].length || 3600,
            };
            formData.invitations = data.invitations.items;

            setResult({ formData, autoGeneratedInvitationIds });
        },
    });

    return {
        ready: !!result,
        ...result,
    };
};

const MeetingEditPage = ({ meetingId }: { meetingId: number }) => {
    const { formData, autoGeneratedInvitationIds, ready } = useInitialMeetingFormData(meetingId);

    if (!ready) return null;

    const summary = <Link to={`/meeting/view/${formData.id}`}>{formData.summary}</Link>;

    return (
        <Page
            title={defineMessage({ id: "title.editMeeting", defaultMessage: "Edit meeting: {summary}" })}
            titleValues={{ summary }}
        >
            <SaveMeetingForm
                initialFormData={formData}
                successNotification={defineMessage({
                    id: "meeting.success.update",
                    defaultMessage: "The meeting has been updated.",
                })}
                autoGeneratedInvitationIds={autoGeneratedInvitationIds}
            />
        </Page>
    );
};

export default MeetingEditPage;
